import { Caption, MediaItem, SpecialEffect } from "../../types";
import { Dispatch, SetStateAction } from 'react';
import { getApiUrl } from '../../config/api';


//only for the media
export async function  fetchMediaFromS3(projectId: string){
    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      };

      const response = await fetch(getApiUrl(`/media/get-media?path=${projectId}`), {
        headers
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching captions:', error);
    }
}

export async function fetchMediaJsonFromS3(projectId: string):Promise<MediaItem[]>{
  try {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };

    const response = await fetch(getApiUrl(`/media/get-all-media-items-json?path=${projectId}`), {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseText = await response.text();
    console.log('📦 [MEDIA SERVICE] Raw response:', responseText.substring(0, 200));
    
    let mediaItems;
    try {
      mediaItems = JSON.parse(responseText);
    } catch (parseError) {
      console.error('❌ [MEDIA SERVICE] JSON Parse Error:', parseError);
      console.error('❌ [MEDIA SERVICE] Invalid JSON:', responseText);
      throw new Error('Invalid JSON response from server');
    }
    
    // Ensure we have an array
    if (!Array.isArray(mediaItems)) {
      console.error('❌ [MEDIA SERVICE] Response is not an array:', typeof mediaItems);
      throw new Error('Expected array of media items');
    }
    
    // Normalize the media items to ensure consistent format
    mediaItems = mediaItems.map((item: any, index: number) => {
      // Use spread operator to preserve ALL fields (including position, scale, and any future additions)
      return {
        ...item, // ✅ Keep ALL fields from backend (position, scale, etc.)
        // Only override/normalize specific fields that might be missing or malformed:
        id: item.id || `${index + 1}`,
        name: item.name || 'Unnamed Media',
        start: item.start || 0,
        end: item.end || 0,
        url: item.url || item.src || '', // Ensure URL is present
        type: item.type || 'image',
        length: item.length || 0,
        src: item.src || item.url || '', // Ensure src is present
        effect: (item.effect as SpecialEffect) || SpecialEffect.NONE,
        hidden: item.hidden || false, // Preserve hidden field
        // position and scale are now automatically preserved via spread operator
      };
    });
    
    console.log(`✅ [MEDIA SERVICE] Fetched ${mediaItems.length} normalized media items from backend`);
    return mediaItems;
  } catch (error) {
    console.error('❌ [MEDIA SERVICE] Error fetching media items:', error);
    return [];
  }
}


import { uploadProjectMedia } from "../../services/mediaService.tsx";
import { it } from "node:test";

export const saveMediaItemToJson = async (
  projectId: string,
  mediaItems: MediaItem[],
): Promise<any> => {
  // First, check for blob URLs that need to be uploaded to S3
  const blobItems = mediaItems.filter(item => 
    (item.url && item.url.startsWith('blob:')) || 
    (item.src && item.src.startsWith('blob:'))
  );
  
  console.log(`Found ${blobItems.length} items with blob URLs that need to be uploaded`);
  
  // If we have blob URLs, we need to upload them first
  if (blobItems.length > 0) {
    try {
      // Convert blob URLs to File objects
      const filePromises = blobItems.map(async (item) => {
        const blobUrl = item.url || item.src;
        if (!blobUrl || !blobUrl.startsWith('blob:')) return null;
        
        console.log(`Converting blob URL to file: ${item.name}`);
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        return new File([blob], item.name, { 
          type: item.type === 'image' ? 'image/png' : 'video/mp4' 
        });
      });
      
      const files = (await Promise.all(filePromises)).filter(Boolean) as File[];
      
      if (files.length > 0) {
        console.log(`Uploading ${files.length} files to S3`);
        // Upload to S3
        const uploadResult = await uploadProjectMedia(files, projectId);
        
        console.log(`Upload successful, updating media items with S3 URLs`);
        
        // Update the media items with the S3 URLs
        let uploadIndex = 0;
        mediaItems = mediaItems.map(item => {
          // If this item has a blob URL
          if ((item.url && item.url.startsWith('blob:')) || 
              (item.src && item.src.startsWith('blob:'))) {
            
            // Get the S3 URL for this file
            const s3Url = uploadResult.urls[uploadIndex];
            uploadIndex++;
            
            return {
              ...item,
              url: s3Url,
              src: s3Url
            };
          }
          return item;
        });
      }
    } catch (error) {
      console.error("Error uploading blob files to S3:", error);
      throw new Error(`Failed to upload media files: ${error.message}`);
    }
  }

  // Convert temporary blob URLs to permanent S3 URLs
  // Blob URLs are temporary in-memory references that need to be replaced with 
  // permanent URLs from S3 storage for persistence
  mediaItems = mediaItems.map(item => {
    // If this item has a blob URL, replace it with the permanent S3 URL
    if ((item.src && item.src.startsWith('blob:'))) {
      
      return {
        ...item,
        src: item.url
      };
    }
    return item;
  });
  
  // Now clean and normalize the data
  const cleanedMediaItems = mediaItems.map((item, index) => {
    // Helper functions to detect file types
    const isImageURL = (url: string) => /\.(jpg|jpeg|png|gif|bmp|webp)($|\?)/i.test(url);
    const isVideoURL = (url: string) => /\.(mp4|webm|mov|avi|wmv)($|\?)/i.test(url);
    
    // Get non-blob URLs for checking
    const srcForChecking = item.src && !item.src.startsWith('blob:') ? item.src : '';
    const urlForChecking = item.url && !item.url.startsWith('blob:') ? item.url : '';
    
    // Determine the true content type based on file extensions
    let correctType = item.type;
    let finalSrc = item.src || '';
    let finalUrl = item.url || '';
    
    // First, try to determine type from src (more reliable)
    if (srcForChecking) {
      if (isImageURL(srcForChecking)) {
        correctType = 'image';
      } else if (isVideoURL(srcForChecking)) {
        correctType = 'video';
      }
    }
    // If no src or can't determine from src, try url
    else if (urlForChecking) {
      if (isImageURL(urlForChecking)) {
        correctType = 'image';
      } else if (isVideoURL(urlForChecking)) {
        correctType = 'video';
      }
    }
    
    // If we have both src and url, and they point to different types, make them consistent
    if (srcForChecking && urlForChecking) {
      const srcIsImage = isImageURL(srcForChecking);
      const srcIsVideo = isVideoURL(srcForChecking);
      const urlIsImage = isImageURL(urlForChecking);
      const urlIsVideo = isVideoURL(urlForChecking);
      
      // If there's a type mismatch between src and url
      if ((srcIsImage && urlIsVideo) || (srcIsVideo && urlIsImage)) {
        console.warn(`URL and SRC type mismatch for item ${index}. Making them consistent.`);
        // Trust src more than url
        finalUrl = srcForChecking;
      }
    }
    
    // Ensure consistent ID format
    const cleanId = typeof item.id === 'string' ? item.id : `${Date.now()}-${index}`;
    
    return {
      ...item,
      id: cleanId,
      url: finalUrl, 
      src: finalSrc,
      type: correctType,
      effect: item.effect || 'none'
    };
  });

  console.log("Saving media items with cleaned data:", cleanedMediaItems);
  const payload = { projectId, mediaItems: cleanedMediaItems };

  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };

  const response = await fetch(getApiUrl("/media/save-media-item-tojson"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Sending media items failed with status: ${response.status}`);
  }
  const result = await response.json();
  console.log("Save result:", result);
  return result.videoUrl;
};


export const captureVideoSnapshot = (videoUrl: string): Promise<{ snapshotUrl: string; duration: number }> => {
  console.log("captureVideoSnapshot -> ", videoUrl);
  return new Promise((resolve, reject) => {
    const videoElement = document.createElement('video');
    videoElement.crossOrigin = 'anonymous'; // Ensure CORS is enabled
    videoElement.src = videoUrl;
    console.log("onloadedmetadata -> 1");
    videoElement.onloadedmetadata = () => {
      const duration = videoElement.duration;
      // Choose snapshot time: if the video is longer than 1 second, take snapshot at 1 second
      const snapshotTime = duration > 1 ? 1 : 0;
      videoElement.currentTime = snapshotTime;
      console.log("Setting snapshout -> 2");
      
      videoElement.onseeked = () => {
        const canvas = document.createElement('canvas');
        
        // Limit canvas size to prevent freezing with high-resolution videos
        const MAX_DIMENSION = 1920; // Max width or height for thumbnail
        const videoWidth = videoElement.videoWidth;
        const videoHeight = videoElement.videoHeight;
        
        // Calculate scale to fit within MAX_DIMENSION while maintaining aspect ratio
        const scale = Math.min(1, MAX_DIMENSION / Math.max(videoWidth, videoHeight));
        
        canvas.width = Math.floor(videoWidth * scale);
        canvas.height = Math.floor(videoHeight * scale);
        
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          const snapshotUrl = canvas.toDataURL('image/jpeg', 0.8); // Use JPEG with 80% quality for faster encoding
          resolve({ snapshotUrl, duration });
        } else {
          reject(new Error('Unable to get canvas context.'));
        }
      };

      videoElement.onerror = (e) => reject(new Error('Error seeking in video element.'));
    };

    videoElement.onerror = (e) => reject(new Error('Error loading video metadata.'));
  });
}


export const updateMediaWithScreenshots = async (data: MediaItem[], setMedia: React.Dispatch<React.SetStateAction<MediaItem[]>>) => {
  try {
    // Create items with proper IDs and correct URL encoding
    const updatedItems = data.map((item, index) => {
      // Replace + with %2B in URLs to prevent encoding issues
      const encodedUrl = item.url.replace(/\+/g, '%2B');
      
      return {
        ...item,
        id: `${Date.now()}-${index}-${item.name}`,
        url: encodedUrl,
        src: encodedUrl, // Use the same URL for src - browser will handle preview for MP4s
        effect: (item.effect as SpecialEffect) || SpecialEffect.NONE
      };
    });
    
    // Update the media state with the processed items
    setMedia(updatedItems);
    console.log(`Processed ${updatedItems.length} media items from library`, updatedItems);
    return updatedItems;
  } catch (error) {
    console.error("Error processing media items:", error);
    throw error;
  }
}




